/**
 * L6a: Rechnung als eigene Seite (/billing/invoices/:id) statt Dialog „Rechnungsdetails“.
 *
 * Aufbau: Kopfzeile · Prüfleiste (EN 16931, X11) mit nächstem Schritt · links Rechnungskopf und
 * Positionen (L1–L3) · rechts PDF-Vorschau (dauerhaft). Unter 1100 px Breite: Reiter statt Spalten.
 * Fachlogik, Statuskette und Berechtigungen unverändert; die Prüfung bleibt serverseitig.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, Ban, Check, CheckCircle2, Download, ExternalLink, FileText,
  History, Pencil, Plus, RefreshCw, RotateCcw, Send, Trash2, ChevronDown, ChevronUp, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { AuditHistoryPanel } from '../components/AuditHistoryPanel';
import InvoicePositionEditor from '../components/InvoicePositionEditor';
import PdfCanvasPreview from '../components/PdfCanvasPreview';
import {
  DEFAULT_PERMISSIONS, mayDo, formatCurrency, formatDate, getReferenceLabel,
  getStatusBadge, getProvenanceInfo, handleApiError, downloadEinvoiceXml,
} from '../components/billing/billingUtils';
import {
  IssueInvoiceDialog, UnapproveInvoiceDialog, VoidInvoiceDialog, DeleteInvoiceDialog,
} from '../components/billing/InvoiceActionDialogs';

// Kopffeld je Referenztyp – wie REFERENCE_TYPE_FIELD im Backend
const REFERENCE_TYPE_FIELD = { po: 'order_ref', contract: 'contract_ref', quote: 'quote_ref' };
const WIDE_QUERY = '(min-width: 1100px)';
const POSITION_IN_MESSAGE = /^Pos\.\s*(\d+):/;
const HEADER_BTS = ['BT-12', 'BT-13', 'BT-14', 'BT-22', 'BT-73', 'BT-74'];

const referenceFieldOf = (invoice) => REFERENCE_TYPE_FIELD[invoice?.reference_type] || 'order_ref';
const referenceValueOf = (invoice) => {
  if (!invoice) return '';
  const value = invoice[referenceFieldOf(invoice)];
  return (value ?? invoice.reference_value ?? '') || '';
};
const headerFromInvoice = (invoice) => ({
  period_start: invoice?.period_start || '',
  period_end: invoice?.period_end || '',
  reference_value: referenceValueOf(invoice),
  invoice_note: invoice?.invoice_note || '',
});

function useIsWide() {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(WIDE_QUERY).matches : true);
  const [wide, setWide] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(WIDE_QUERY);
    const onChange = () => setWide(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return wide;
}

export default function InvoiceDraftPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const isWide = useIsWide();
  const [mobileTab, setMobileTab] = useState('positions');

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const may = useCallback((action) => mayDo(user, permissions, action), [user, permissions]);

  // Prüfbericht (X11)
  const [report, setReport] = useState(null);
  const [checking, setChecking] = useState(false);
  const [findingsOpen, setFindingsOpen] = useState(false);
  const [highlightPos, setHighlightPos] = useState(null);

  // Rechnungskopf
  const [header, setHeader] = useState(headerFromInvoice(null));
  const [savingHeader, setSavingHeader] = useState(false);

  // PDF
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfFilename, setPdfFilename] = useState('Rechnung.pdf');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfStale, setPdfStale] = useState(false);
  const pdfUrlRef = useRef(null);

  // Inline-Panels und Dialoge
  const [panel, setPanel] = useState('none'); // 'none' | 'approve' | 'send'
  const [approveNote, setApproveNote] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [sentChannel, setSentChannel] = useState('');
  const [sentNote, setSentNote] = useState('');
  const [isMarkingSent, setIsMarkingSent] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueFindings, setIssueFindings] = useState([]);
  const [isIssuing, setIsIssuing] = useState(false);
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);
  const [unapproveReason, setUnapproveReason] = useState('');
  const [isUnapproving, setIsUnapproving] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Positionen
  const [editLineItemOpen, setEditLineItemOpen] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [isChangingSplit, setIsChangingSplit] = useState(false);
  const [newManualItem, setNewManualItem] = useState({ description: '', quantity: 1, unit: 'Stunden', unit_price: 0 });

  const status = invoice?.status;
  const isDraft = status === 'draft';
  const editable = isDraft && may('edit');
  const isFinal = ['issued', 'sent', 'void', 'voided'].includes(status);

  // ---------- Laden ----------
  const loadInvoice = useCallback(async () => {
    const response = await api.get(`/billing/invoices/${id}`);
    setInvoice(response.data);
    setHeader(headerFromInvoice(response.data));
    return response.data;
  }, [api, id]);

  const runCheck = useCallback(async () => {
    setChecking(true);
    try {
      const response = await api.get(`/billing/invoices/${id}/einvoice-check`);
      setReport(response.data);
    } catch (error) {
      setReport(null);
      handleApiError(error);
    } finally {
      setChecking(false);
    }
  }, [api, id]);

  const loadPdf = useCallback(async () => {
    setPdfLoading(true);
    setPdfError(false);
    try {
      const response = await api.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Rechnung.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
      const blobUrl = URL.createObjectURL(blob);
      pdfUrlRef.current = blobUrl;
      setPdfBlob(blob);
      setPdfBlobUrl(blobUrl);
      setPdfFilename(filename);
      setPdfStale(false);
    } catch (error) {
      setPdfError(true);
      toast.error('Das PDF konnte nicht erzeugt werden.');
    } finally {
      setPdfLoading(false);
    }
  }, [api, id]);

  useEffect(() => () => {
    if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
  }, []);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const [, permissionsRes] = await Promise.all([
          loadInvoice(),
          api.get('/company-settings/billing-permissions').catch(() => ({ data: DEFAULT_PERMISSIONS })),
        ]);
        if (cancelled) return;
        setPermissions(permissionsRes.data || DEFAULT_PERMISSIONS);
        runCheck();
        loadPdf();
      } catch (error) {
        if (cancelled) return;
        if (error.response?.status === 404) setNotFound(true);
        else handleApiError(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, id, loadInvoice, runCheck, loadPdf]);

  // Nach jeder gespeicherten Änderung: Rechnung neu laden, prüfen, PDF als veraltet markieren
  const afterChange = useCallback(async ({ statusChanged = false } = {}) => {
    try {
      const fresh = await loadInvoice();
      runCheck();
      const final = ['issued', 'sent', 'void', 'voided'].includes(fresh.status);
      if (statusChanged && final) {
        loadPdf(); // abgelegte Datei sofort zeigen
      } else {
        setPdfStale(true);
      }
    } catch (error) {
      handleApiError(error);
    }
  }, [loadInvoice, runCheck, loadPdf]);

  // ---------- Rechnungskopf ----------
  const savedHeader = headerFromInvoice(invoice);
  const headerDirty = invoice && (
    header.period_start !== savedHeader.period_start ||
    header.period_end !== savedHeader.period_end ||
    header.reference_value.trim() !== savedHeader.reference_value ||
    header.invoice_note.trim() !== savedHeader.invoice_note
  );
  const requireReference = !!invoice?.invoice_options?.require_reference;
  const referenceMissing = requireReference && !header.reference_value.trim();

  const handleSaveHeader = async () => {
    if (!invoice || !headerDirty) return;
    const payload = {};
    if (header.period_start !== savedHeader.period_start || header.period_end !== savedHeader.period_end) {
      payload.period_start = header.period_start;
      payload.period_end = header.period_end;
    }
    if (header.reference_value.trim() !== savedHeader.reference_value) payload.reference_value = header.reference_value;
    if (header.invoice_note.trim() !== savedHeader.invoice_note) payload.invoice_note = header.invoice_note;
    setSavingHeader(true);
    try {
      await api.put(`/billing/invoices/${invoice.id}/header`, payload);
      toast.success('Rechnungskopf gespeichert');
      await afterChange();
    } catch (error) {
      handleApiError(error);
    } finally {
      setSavingHeader(false);
    }
  };

  // ---------- Positionen ----------
  const currentSplit = invoice
    ? (invoice.position_split || invoice.invoice_options?.position_split || 'combined')
    : 'combined';
  const handleChangeSplit = async (mode) => {
    if (!invoice || mode === currentSplit) return;
    setIsChangingSplit(true);
    try {
      await api.put(`/billing/invoices/${invoice.id}/position-split`, { position_split: mode });
      toast.success(mode === 'per_activity' ? 'Eine Position je Tätigkeit' : 'Eine Position mit Tätigkeiten im Text');
      await afterChange();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsChangingSplit(false);
    }
  };

  const addManualItemToInvoice = async () => {
    if (!newManualItem.description.trim()) {
      toast.error('Bitte geben Sie eine Beschreibung ein');
      return;
    }
    try {
      await api.post(`/billing/invoices/${invoice.id}/manual-items`, newManualItem);
      setNewManualItem({ description: '', quantity: 1, unit: 'Stunden', unit_price: 0 });
      toast.success('Position hinzugefügt');
      await afterChange();
    } catch (error) {
      handleApiError(error);
    }
  };

  const openEditLineItem = (item) => {
    setEditingLineItem(item);
    setEditLineItemOpen(true);
  };

  const jumpToPosition = (pos) => {
    if (!isWide) setMobileTab('positions');
    setHighlightPos(pos);
    setTimeout(() => {
      const el = document.getElementById(`position-${pos}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    setTimeout(() => setHighlightPos(null), 2000);
  };
  const jumpToHeader = () => {
    if (!isWide) setMobileTab('positions');
    const el = document.getElementById('rechnungskopf');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---------- Statusaktionen ----------
  const handleApproveInvoice = async () => {
    setIsApproving(true);
    try {
      await api.post(`/billing/invoices/${invoice.id}/approve`, { note: approveNote || undefined });
      toast.success('Rechnung freigegeben');
      setPanel('none');
      setApproveNote('');
      await afterChange({ statusChanged: true });
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleUnapproveInvoice = async () => {
    if (unapproveReason.length < 5) {
      toast.error('Bitte geben Sie einen Grund ein (mindestens 5 Zeichen)');
      return;
    }
    setIsUnapproving(true);
    try {
      await api.post(`/billing/invoices/${invoice.id}/unapprove`, { reason: unapproveReason });
      toast.success('Freigabe zurückgezogen');
      setUnapproveDialogOpen(false);
      await afterChange({ statusChanged: true });
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsUnapproving(false);
    }
  };

  const openIssueDialog = () => {
    setIssueFindings([]);
    setIssueDialogOpen(true);
  };

  const handleIssueInvoice = async () => {
    setIsIssuing(true);
    setIssueFindings([]);
    try {
      const response = await api.post(`/billing/invoices/${invoice.id}/mark-issued`);
      toast.success(response.data.message === 'Dokumente nachgeholt'
        ? `Dokumente zu Rechnung ${response.data.invoice_number} erzeugt`
        : `Rechnung ${response.data.invoice_number} ausgestellt`);
      if (response.data.pdf_error) {
        toast.error('Die Rechnung wurde ausgestellt, XML und PDF konnten jedoch nicht erzeugt werden. Bitte erneut „Ausstellen“ – die Nummer bleibt.');
      }
      setIssueDialogOpen(false);
      await afterChange({ statusChanged: true });
    } catch (error) {
      // Der Axios-Interceptor (AuthContext) macht aus einem Objekt mit message einen String und legt das Original in detail_raw ab
      const detail = error.response?.data?.detail_raw || error.response?.data?.detail;
      if (error.response?.status === 422 && detail && typeof detail === 'object' && Array.isArray(detail.findings)) {
        setIssueFindings(detail.findings);
        toast.error(detail.message || 'Die Rechnung besteht die Prüfung nicht.');
      } else {
        handleApiError(error);
      }
    } finally {
      setIsIssuing(false);
    }
  };

  const handleMarkSent = async () => {
    setIsMarkingSent(true);
    try {
      await api.post(`/billing/invoices/${invoice.id}/mark-sent`, {
        channel: sentChannel || undefined,
        note: sentNote || undefined,
      });
      toast.success('Rechnung als versendet markiert');
      setPanel('none');
      await afterChange({ statusChanged: true });
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsMarkingSent(false);
    }
  };

  const handleVoidInvoice = async () => {
    setIsVoiding(true);
    try {
      const response = await api.post(`/billing/invoices/${invoice.id}/void`, { cancellation_reason: voidReason });
      if (response.data.cancellation_invoice_number) {
        toast.success(`Stornorechnung ${response.data.cancellation_invoice_number} erstellt`);
      } else {
        toast.success('Rechnung storniert');
      }
      setVoidDialogOpen(false);
      if (response.data.cancellation_invoice_id) {
        navigate(`/billing/invoices/${response.data.cancellation_invoice_id}`);
      } else {
        // Entwurf storniert (Einträge wieder offen) – zurück zur Liste
        navigate('/billing');
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsVoiding(false);
    }
  };

  const handleDeleteInvoice = async () => {
    setIsDeleting(true);
    try {
      const response = await api.delete(`/billing/invoices/${invoice.id}`);
      const entriesReleased = (response.data.entries_released || 0) + (response.data.mileage_released || 0);
      toast.success(`Entwurf gelöscht. ${entriesReleased} Einträge wieder zur Abrechnung freigegeben.`);
      setDeleteDialogOpen(false);
      navigate('/billing');
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const downloadPdf = () => {
    if (pdfBlobUrl) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = pdfFilename;
      link.click();
    }
  };

  // ---------- Ableitungen für die Prüfleiste ----------
  const check = report?.check || null;
  const errorCount = check?.errors || 0;
  const warningCount = check?.warnings || 0;
  const findings = check?.findings || [];
  const barBorder = !check ? 'border-l-border' : errorCount > 0 ? 'border-l-red-500' : warningCount > 0 ? 'border-l-amber-500' : 'border-l-emerald-500';

  let primary = null;    // { label, icon, onClick, className, disabled }
  const secondary = [];  // [{ label, icon, onClick, className }]
  let nextHint = '';
  if (invoice) {
    if (isDraft) {
      if (may('approve')) {
        primary = {
          label: 'Freigeben', icon: CheckCircle2, className: 'bg-blue-600 hover:bg-blue-700',
          onClick: () => setPanel(panel === 'approve' ? 'none' : 'approve'), disabled: errorCount > 0 || checking,
        };
      }
      if (errorCount > 0) nextHint = `${errorCount} Fehler beheben, dann freigeben`;
      else if (headerDirty) nextHint = 'Kopf noch nicht gespeichert';
      else nextHint = may('approve') ? 'Bereit zur Freigabe' : 'Bereit – Freigabe durch Vorgesetzte';
      if (may('delete')) secondary.push({ label: 'Entwurf löschen', icon: Trash2, onClick: () => setDeleteDialogOpen(true), className: 'text-red-500 border-red-500/30 hover:bg-red-500/10' });
    } else if (status === 'approved') {
      if (may('issue')) {
        primary = { label: 'Ausstellen', icon: Check, className: 'bg-emerald-600 hover:bg-emerald-700', onClick: openIssueDialog };
      }
      nextHint = 'Freigegeben – die Nummer wird beim Ausstellen vergeben';
      if (may('approve')) secondary.push({ label: 'Freigabe zurückziehen', icon: RotateCcw, onClick: () => { setUnapproveReason(''); setUnapproveDialogOpen(true); } });
      if (may('delete')) secondary.push({ label: 'Löschen', icon: Trash2, onClick: () => setDeleteDialogOpen(true), className: 'text-red-500 border-red-500/30 hover:bg-red-500/10' });
    } else if (status === 'issued' && !invoice.is_cancellation) {
      if (may('send')) {
        primary = {
          label: 'Als versendet markieren', icon: Send, className: 'bg-violet-600 hover:bg-violet-700',
          onClick: () => { if (panel !== 'send') { setSentChannel(''); setSentNote(''); } setPanel(panel === 'send' ? 'none' : 'send'); },
        };
      }
      nextHint = 'Ausgestellt';
      if (may('void')) secondary.push({ label: 'Stornieren', icon: Ban, onClick: () => { setVoidReason(''); setVoidDialogOpen(true); }, className: 'text-red-500 border-red-500/30 hover:bg-red-500/10' });
    } else if (status === 'sent' && !invoice.is_cancellation) {
      nextHint = `Versendet${invoice.sent_channel ? ` (${invoice.sent_channel})` : ''}`;
      if (may('void')) secondary.push({ label: 'Stornieren', icon: Ban, onClick: () => { setVoidReason(''); setVoidDialogOpen(true); }, className: 'text-red-500 border-red-500/30 hover:bg-red-500/10' });
    } else if (invoice.is_cancellation) {
      nextHint = 'Stornorechnung';
    } else {
      nextHint = 'Storniert – nur lesen';
    }
  }

  // ---------- Darstellung ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (notFound || !invoice) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <Link to="/billing" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Abrechnung
        </Link>
        <p className="text-muted-foreground">Rechnung nicht gefunden.</p>
      </div>
    );
  }

  const PrimaryIcon = primary?.icon;

  const checkBar = (
    <div className={`bg-card border border-border border-l-4 ${barBorder} rounded-lg p-3 space-y-2`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span className={`w-2.5 h-2.5 rounded-full ${errorCount > 0 ? 'bg-red-500' : 'bg-emerald-500'}`} />
          {checking && !check ? 'Prüfung läuft…' : `${errorCount} Fehler`}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
          {warningCount} Hinweise
        </div>
        {findings.length > 0 && (
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs border-border" onClick={() => setFindingsOpen(!findingsOpen)}>
            {findingsOpen ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
            {findingsOpen ? 'Befunde ausblenden' : 'Befunde anzeigen'}
          </Button>
        )}
        <span className="text-xs text-muted-foreground">
          Prüfung EN 16931 ({report?.stage === 'issue' ? 'ausgestellt' : 'Entwurf'}){checking ? ' · wird aktualisiert…' : ''}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {nextHint && <span className="text-xs text-muted-foreground">{nextHint}</span>}
          {secondary.map((s) => {
            const Icon = s.icon;
            return (
              <Button key={s.label} type="button" variant="outline" size="sm" onClick={s.onClick} className={`border-border ${s.className || ''}`}>
                <Icon className="w-4 h-4 mr-2" />
                {s.label}
              </Button>
            );
          })}
          {primary && (
            <Button type="button" size="sm" onClick={primary.onClick} disabled={primary.disabled} className={primary.className}>
              <PrimaryIcon className="w-4 h-4 mr-2" />
              {primary.label}
            </Button>
          )}
        </div>
      </div>
      {findingsOpen && findings.length > 0 && (
        <ul className="border-t border-border pt-2 space-y-1 text-xs">
          {findings.map((f, i) => {
            const posMatch = (f.message || '').match(POSITION_IN_MESSAGE);
            const isHeader = !posMatch && (HEADER_BTS.includes(f.bt) || f.rule === 'CTI-REF');
            return (
              <li key={i} className="flex items-start gap-2">
                <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${f.severity === 'fehler' ? 'bg-red-500' : f.severity === 'warnung' ? 'bg-amber-500' : 'bg-zinc-400'}`} />
                <span className={f.severity === 'fehler' ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground'}>
                  <span className="font-mono">{f.rule}</span>{f.bt ? ` (${f.bt})` : ''}: {f.message}
                </span>
                {posMatch && (
                  <button type="button" className="ml-auto whitespace-nowrap underline text-foreground" onClick={() => jumpToPosition(Number(posMatch[1]))}>
                    Zur Position {posMatch[1]}
                  </button>
                )}
                {isHeader && (
                  <button type="button" className="ml-auto whitespace-nowrap underline text-foreground" onClick={jumpToHeader}>
                    Zum Kopf
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const approvePanel = panel === 'approve' && isDraft && may('approve') && (
    <div className="bg-card border border-blue-500/40 rounded-lg p-4 space-y-3">
      <p className="font-medium text-foreground">Rechnung freigeben</p>
      <p className="text-sm text-muted-foreground">
        Prüfbericht, Positionen und PDF stehen auf dieser Seite. Nach der Freigabe kann die Buchhaltung ausstellen; Änderungen am Entwurf sind dann erst nach „Freigabe zurückziehen“ möglich.
      </p>
      {headerDirty && (
        <p className="text-xs text-amber-700 dark:text-amber-400">Der Rechnungskopf hat ungespeicherte Änderungen – sie werden nicht mit freigegeben.</p>
      )}
      <div className="space-y-2">
        <Label className="text-muted-foreground">Anmerkung (optional)</Label>
        <Textarea
          value={approveNote}
          onChange={(e) => setApproveNote(e.target.value)}
          placeholder="z.B. Inhaltlich geprüft"
          rows={2}
          className="bg-input border-border text-foreground"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => setPanel('none')}>Abbrechen</Button>
        <Button type="button" onClick={handleApproveInvoice} disabled={isApproving || errorCount > 0} className="bg-blue-600 hover:bg-blue-700">
          {isApproving ? 'Freigeben...' : 'Freigeben'}
        </Button>
      </div>
    </div>
  );

  const sendPanel = panel === 'send' && status === 'issued' && may('send') && (
    <div className="bg-card border border-violet-500/40 rounded-lg p-4 space-y-3">
      <p className="font-medium text-foreground">Als versendet markieren</p>
      <p className="text-sm text-muted-foreground">
        Markiert die Rechnung als an den Kunden übermittelt. Es wird kein tatsächlicher Versand ausgelöst.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label className="text-muted-foreground">Versandweg</Label>
          <Select value={sentChannel} onValueChange={setSentChannel}>
            <SelectTrigger className="bg-input border-border text-foreground">
              <SelectValue placeholder="Auswählen..." />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="E-Mail">E-Mail</SelectItem>
              <SelectItem value="Post">Post</SelectItem>
              <SelectItem value="Persönlich übergeben">Persönlich übergeben</SelectItem>
              <SelectItem value="Sonstiges">Sonstiges</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className="text-muted-foreground">Anmerkung (optional)</Label>
          <Input
            value={sentNote}
            onChange={(e) => setSentNote(e.target.value)}
            placeholder="z.B. An buchhaltung@kunde.de gesendet"
            className="bg-input border-border text-foreground"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => setPanel('none')}>Abbrechen</Button>
        <Button type="button" onClick={handleMarkSent} disabled={isMarkingSent} className="bg-violet-600 hover:bg-violet-700">
          {isMarkingSent ? 'Markiere...' : 'Als versendet markieren'}
        </Button>
      </div>
    </div>
  );

  const headerCard = (
    <div id="rechnungskopf" className="bg-card border border-border rounded-lg p-4 space-y-4 scroll-mt-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-foreground">Rechnungskopf</p>
        {editable && (
          <Button type="button" size="sm" onClick={handleSaveHeader} disabled={!headerDirty || savingHeader} className="bg-cyan-600 hover:bg-cyan-700">
            <Save className="w-4 h-4 mr-2" />
            {savingHeader ? 'Speichern...' : 'Kopf speichern'}
          </Button>
        )}
      </div>
      {invoice.period_warning && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-amber-600 dark:text-amber-400 text-sm">{invoice.period_warning}</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs">Leistungszeitraum von</Label>
          <Input type="date" value={header.period_start} disabled={!editable}
            onChange={(e) => setHeader({ ...header, period_start: e.target.value })}
            className="bg-input border-border text-foreground" />
        </div>
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs">bis</Label>
          <Input type="date" value={header.period_end} disabled={!editable}
            onChange={(e) => setHeader({ ...header, period_end: e.target.value })}
            className="bg-input border-border text-foreground" />
        </div>
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs">
            {getReferenceLabel(invoice.reference_type || 'po')}{requireReference && ' *'}
          </Label>
          <Input value={header.reference_value} disabled={!editable}
            onChange={(e) => setHeader({ ...header, reference_value: e.target.value })}
            placeholder={requireReference ? 'Erforderlich' : 'Optional'}
            className={`bg-input text-foreground ${referenceMissing ? 'border-red-500' : 'border-border'}`} />
          {referenceMissing && <p className="text-xs text-red-500">Beim Kunden als Pflicht hinterlegt</p>}
        </div>
        <div className="space-y-1">
          <Label className="text-muted-foreground text-xs">Bankkonto</Label>
          <p className="text-sm text-foreground pt-2">
            {invoice.bank_snapshot
              ? <>{invoice.bank_snapshot.label} · <span className="font-mono">{invoice.bank_snapshot.iban_display}</span></>
              : (report?.check?.bank?.label || '–')}
          </p>
          {invoice.bank_snapshot && !isFinal && (
            <p className="text-xs text-muted-foreground">wird beim Ausstellen festgeschrieben</p>
          )}
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-muted-foreground text-xs">Einleitungstext (optional)</Label>
        <Textarea value={header.invoice_note} disabled={!editable}
          onChange={(e) => setHeader({ ...header, invoice_note: e.target.value })}
          placeholder="Erscheint unter dem Leistungszeitraum..."
          rows={2}
          className="bg-input border-border text-foreground" />
      </div>
      {editable && headerDirty && (
        <p className="text-xs text-amber-700 dark:text-amber-400">Ungespeicherte Änderungen – „Kopf speichern“. Erzeugte Positionstexte übernehmen den neuen Zeitraum; von Hand geänderte Texte werden als „Text veraltet“ markiert.</p>
      )}
      {/* X8: Stand der E-Rechnung */}
      {invoice.invoice_number && (
        invoice.einvoice_xml_sha256 ? (
          <p className="text-xs text-emerald-700 dark:text-emerald-400">
            E-Rechnung: ZUGFeRD-PDF (PDF/A-3, Profil {invoice.einvoice_conformance_level || 'EN 16931'}) mit eingebettetem XML abgelegt
            {invoice.pdf_sha256 ? ` · SHA-256 ${invoice.pdf_sha256.slice(0, 12)}…` : ''}
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {invoice.einvoice_error
                ? `XML und PDF fehlen: ${invoice.einvoice_error}`
                : 'Für diese Rechnung ist kein XML abgelegt (vor X8 ausgestellt).'}
            </p>
            {invoice.einvoice_error && may('issue') && (
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs border-border" onClick={handleIssueInvoice} disabled={isIssuing}>
                Dokumente nachholen
              </Button>
            )}
          </div>
        )
      )}
    </div>
  );

  const positionsCard = (
    <div className="bg-card border border-border rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-foreground">Rechnungspositionen</p>
        {editable && ((invoice.line_items || []).some((i) => i.category_id) || invoice.position_split) && (
          <div className="flex gap-1 p-0.5 rounded-md border border-border bg-muted/40" role="group" aria-label="Aufteilung der Betreuungsstunden">
            <Button type="button" size="sm" variant={currentSplit === 'combined' ? 'secondary' : 'ghost'} className="h-7 text-xs"
              disabled={isChangingSplit} onClick={() => handleChangeSplit('combined')}>
              Eine Position
            </Button>
            <Button type="button" size="sm" variant={currentSplit === 'per_activity' ? 'secondary' : 'ghost'} className="h-7 text-xs"
              disabled={isChangingSplit} onClick={() => handleChangeSplit('per_activity')}>
              Je Tätigkeit
            </Button>
          </div>
        )}
      </div>

      {(invoice.line_items || []).length > 0 && (
        <div className="divide-y divide-border border border-border rounded-lg overflow-hidden">
          {invoice.line_items.map((item, idx) => (
            <div
              key={idx}
              id={`position-${item.position}`}
              className={`p-3 scroll-mt-24 transition-colors ${highlightPos === item.position ? 'bg-amber-500/20' : item.type === 'surcharge' ? 'bg-amber-500/5' : 'bg-muted/30'}`}
            >
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-muted-foreground text-xs">{item.position}.</span>
                    <p className="text-foreground font-medium">{item.title}</p>
                    {(item.title_edited || item.description_edited) && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-cyan-500/20 text-cyan-400">bearbeitet</span>
                    )}
                    {item.description_outdated && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-amber-500/20 text-amber-700 dark:text-amber-300">Text veraltet</span>
                    )}
                    {item.adjusted_entries > 0 && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-amber-500/15 text-amber-700 dark:text-amber-300">{item.adjusted_entries} angepasst</span>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-muted-foreground text-sm mt-1 whitespace-pre-wrap">{item.description}</p>
                  )}
                  <p className="text-muted-foreground text-xs mt-1">
                    {item.quantity} {item.unit} × {formatCurrency(item.unit_price)} · USt. {item.vat_rate}%
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <p className={`font-mono text-right ${item.type === 'surcharge' ? 'text-amber-600 dark:text-amber-400' : 'text-lime-600 dark:text-lime-400'}`}>
                    {item.type === 'surcharge' ? '+' : ''}{formatCurrency(item.net_amount)}
                  </p>
                  {(item.source_entry_ids?.length > 0 || editable) && (
                    <Button type="button" variant="ghost" size="icon" className="w-7 h-7 hover:bg-accent"
                      aria-label={editable ? `Position ${item.position} bearbeiten` : `Buchungen zu Position ${item.position}`}
                      onClick={() => openEditLineItem(item)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add manual item (only for draft) */}
      {editable && (
        <div className="space-y-2 border-t border-border pt-3">
          <Label className="text-muted-foreground text-sm">Manuelle Position hinzufügen</Label>
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-12 sm:col-span-5">
              <Input placeholder="Beschreibung" value={newManualItem.description}
                onChange={(e) => setNewManualItem({ ...newManualItem, description: e.target.value })}
                className="bg-input border-border text-foreground text-sm" />
            </div>
            <div className="col-span-4 sm:col-span-2">
              <Input type="number" min="0.25" step="0.25" placeholder="Menge" value={newManualItem.quantity}
                onChange={(e) => setNewManualItem({ ...newManualItem, quantity: parseFloat(e.target.value) || 0 })}
                className="bg-input border-border text-foreground text-sm" />
            </div>
            <div className="col-span-4 sm:col-span-2">
              <Select value={newManualItem.unit} onValueChange={(value) => setNewManualItem({ ...newManualItem, unit: value })}>
                <SelectTrigger className="bg-input border-border text-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Stunden">Stunden</SelectItem>
                  <SelectItem value="Stück">Stück</SelectItem>
                  <SelectItem value="Pauschale">Pauschale</SelectItem>
                  <SelectItem value="km">km</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3 sm:col-span-2">
              <Input type="number" min="0" step="0.01" placeholder="Preis" value={newManualItem.unit_price || ''}
                onChange={(e) => setNewManualItem({ ...newManualItem, unit_price: parseFloat(e.target.value) || 0 })}
                className="bg-input border-border text-foreground text-sm" />
            </div>
            <div className="col-span-1">
              <Button type="button" onClick={addManualItemToInvoice} size="sm" className="w-full bg-amber-600 hover:bg-amber-700" aria-label="Manuelle Position hinzufügen">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Breakdown and Totals */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Nettobetrag</span>
          <span className="font-mono text-foreground">{formatCurrency(invoice.net_total)}</span>
        </div>
        {invoice.vat_breakdown?.map((vb, idx) => (
          <div key={idx} className="flex justify-between text-sm">
            <span className="text-muted-foreground">USt. {vb.rate}%</span>
            <span className="font-mono text-foreground">{formatCurrency(vb.vat)}</span>
          </div>
        ))}
        <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
          <span className="text-foreground">Rechnungsbetrag</span>
          <span className="font-mono text-cyan-400">{formatCurrency(invoice.gross_total)}</span>
        </div>
      </div>
    </div>
  );

  const pdfCard = (
    <div className="bg-card border border-border rounded-lg p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-foreground text-sm flex items-center gap-2">
          <FileText className="w-4 h-4" />
          {isFinal ? 'Abgelegtes PDF/A-3' : 'PDF-Vorschau'}
        </p>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" className="border-border" onClick={() => window.open(pdfBlobUrl, '_blank')} disabled={!pdfBlobUrl}>
            <ExternalLink className="w-4 h-4 mr-2" />
            Neuer Tab
          </Button>
          <Button type="button" variant="outline" size="sm" className="border-border" onClick={downloadPdf} disabled={!pdfBlobUrl}>
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => downloadEinvoiceXml(api, invoice)}
            className="border-border"
            title={invoice.invoice_number ? 'XML aus dem ZUGFeRD-PDF (factur-x.xml)' : 'Vorschau des XML mit voraussichtlichen Werten'}
          >
            <Download className="w-4 h-4 mr-2" />
            {invoice.invoice_number ? 'XML (E-Rechnung)' : 'XML-Vorschau'}
          </Button>
        </div>
      </div>
      {pdfStale && !isFinal && (
        <div className="flex flex-wrap items-center gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-sm text-amber-700 dark:text-amber-300">
          <span>Vorschau veraltet – der Entwurf wurde geändert.</span>
          <Button type="button" size="sm" className="ml-auto h-7 bg-amber-600 hover:bg-amber-700" onClick={loadPdf} disabled={pdfLoading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${pdfLoading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      )}
      {pdfLoading && !pdfBlob ? (
        <div className="flex items-center justify-center h-[75vh]">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : pdfBlob ? (
        <PdfCanvasPreview
          blob={pdfBlob}
          fallback={
            <div className="flex flex-col items-center justify-center text-muted-foreground gap-4">
              <p className="text-sm">Die Vorschau konnte nicht geladen werden.</p>
              <Button type="button" variant="outline" onClick={() => window.open(pdfBlobUrl, '_blank')} className="border-border">
                <ExternalLink className="w-4 h-4 mr-2" />
                In neuem Tab öffnen
              </Button>
            </div>
          }
        />
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground gap-3">
          <p className="text-sm">{pdfError ? 'Das PDF konnte nicht erzeugt werden.' : 'Kein PDF verfügbar'}</p>
          <Button type="button" variant="outline" size="sm" className="border-border" onClick={loadPdf}>
            <RefreshCw className="w-4 h-4 mr-2" /> Erneut versuchen
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Kopfzeile */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/billing" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Abrechnung
          </Link>
          <h1 className="text-xl font-bold text-foreground">
            {invoice.customer_name} · {formatDate(invoice.period_start)} – {formatDate(invoice.period_end)}
          </h1>
          {getStatusBadge(invoice)}
          {invoice.is_test_invoice && (
            <span className="px-2 py-0.5 text-xs rounded bg-amber-500/20 text-amber-600 border border-amber-500/30">Test</span>
          )}
          {invoice.is_cancellation && (
            <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-600 border border-red-500/30">Storno</span>
          )}
          <span className="text-xs text-muted-foreground">
            {invoice.invoice_number ? `Rechnung Nr. ${invoice.invoice_number}` : `${invoice.draft_reference} · Rechnungsnummer erst beim Ausstellen`}
          </span>
          <Button type="button" variant="outline" size="sm" className="ml-auto border-border" onClick={() => setHistoryOpen(true)}>
            <History className="w-4 h-4 mr-2" />
            Verlauf
          </Button>
        </div>
        {invoice.is_cancellation && invoice.cancels_invoice_number && (
          <p className="text-red-400 text-xs">Storniert: {invoice.cancels_invoice_number}</p>
        )}
        {getProvenanceInfo(invoice) && (
          <p className="text-muted-foreground text-xs">{getProvenanceInfo(invoice)}</p>
        )}
      </div>

      {checkBar}
      {approvePanel}
      {sendPanel}

      {isWide ? (
        <div className="grid grid-cols-2 gap-4 items-start">
          <div className="space-y-4 min-w-0">
            {headerCard}
            {positionsCard}
          </div>
          <div className="sticky top-4 min-w-0">
            {pdfCard}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-1 border-b border-border" role="tablist">
            {[['positions', 'Positionen'], ['preview', 'Vorschau']].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={mobileTab === key}
                onClick={() => setMobileTab(key)}
                className={`px-4 py-2 text-sm -mb-px border-b-2 ${mobileTab === key ? 'border-cyan-500 text-foreground font-medium' : 'border-transparent text-muted-foreground'}`}
              >
                {label}{key === 'preview' && pdfStale && !isFinal ? ' •' : ''}
              </button>
            ))}
          </div>
          {mobileTab === 'positions' ? (
            <div className="space-y-4">
              {headerCard}
              {positionsCard}
            </div>
          ) : pdfCard}
        </div>
      )}

      {/* Position bearbeiten / Buchungen (L1–L3) */}
      <InvoicePositionEditor
        open={editLineItemOpen}
        onOpenChange={setEditLineItemOpen}
        invoice={invoice}
        position={editingLineItem?.position}
        api={api}
        canEdit={may('edit')}
        onInvoiceChanged={(inv) => {
          setInvoice((prev) => ({ ...(prev || {}), ...inv }));
          runCheck();
          setPdfStale(true);
        }}
      />

      <IssueInvoiceDialog
        open={issueDialogOpen}
        onOpenChange={setIssueDialogOpen}
        invoice={invoice}
        findings={issueFindings}
        isIssuing={isIssuing}
        onConfirm={handleIssueInvoice}
      />
      <UnapproveInvoiceDialog
        open={unapproveDialogOpen}
        onOpenChange={setUnapproveDialogOpen}
        reason={unapproveReason}
        onReasonChange={setUnapproveReason}
        isUnapproving={isUnapproving}
        onConfirm={handleUnapproveInvoice}
      />
      <VoidInvoiceDialog
        open={voidDialogOpen}
        onOpenChange={setVoidDialogOpen}
        reason={voidReason}
        onReasonChange={setVoidReason}
        isVoiding={isVoiding}
        onConfirm={handleVoidInvoice}
      />
      <DeleteInvoiceDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        invoice={invoice}
        isDeleting={isDeleting}
        onConfirm={handleDeleteInvoice}
      />

      {/* Audit History Panel Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Änderungshistorie
            </DialogTitle>
          </DialogHeader>
          {historyOpen && (
            <AuditHistoryPanel
              collectionName="invoices"
              documentId={invoice.id}
              isOpen={true}
              compact={false}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
